/**
 * Module AuthGate - Cổng đăng nhập dùng chung cho TOÀN BỘ Workspace
 *
 * Mọi chức năng (Trang chủ, Sheets/Gmail/Zalo đọc truyện, CapCut/Canva video, Emulator, Figma,
 * VS Code, Calendar/Caro, tìm kiếm, TTS...) chỉ dùng được sau khi đăng nhập Google.
 *
 * Khi chưa đăng nhập:
 *  - Màn hình đăng nhập #auth-gate (có sẵn trong index.html) phủ toàn trang.
 *  - Phần còn lại của trang bị `inert` (không click / focus được) và phím tắt của app bị chặn.
 *    Riêng SecurityGuard (lắng nghe ở pha capture của window) vẫn hoạt động như cũ.
 *  - AppRouter chưa điều hướng nên không app nào được mở, không API nào được gọi.
 *
 * Phiên đăng nhập dùng chung với Caro (/api/auth/*). Máy chủ cũng chặn mọi /api/* khi chưa đăng nhập
 * (functions/api/_middleware.js), cổng này chỉ là lớp giao diện.
 */

const RETURN_URL_KEY = 'auth_gate_return_url';
const RETURN_URL_TTL_MS = 10 * 60 * 1000;
const RECHECK_INTERVAL_MS = 5000;
const ME_ENDPOINT = '/api/auth/me';
const LOGIN_ENDPOINT = '/api/auth/google';
const GATE_TITLE = 'Đăng nhập - Coogle Wordspeca';

/**
 * Hash hợp lệ để máy chủ redirect về sau khi đăng nhập (cùng quy tắc với safeReturnTo ở functions/_shared/auth.js).
 * @param {string} hash
 * @returns {string}
 */
export function safeReturnHash(hash) {
  if (
    typeof hash !== 'string'
    || hash.length > 512
    || !hash.startsWith('#/')
    || hash.startsWith('#//')
    || /[\u0000-\u001f\\]/.test(hash)
  ) {
    return '#/home';
  }
  return hash;
}

/**
 * Sau khi đăng nhập, Google chỉ trả về được phần hash (#/...). Link chia sẻ cũ dạng query
 * (?source=...&story=...&chap=... hoặc ?mode=video&v=...) sẽ bị mất. Hàm này khôi phục lại
 * URL đầy đủ đã lưu trước khi chuyển sang Google. Gọi trước khi các module đọc URL.
 */
export function restorePendingReturnUrl() {
  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(RETURN_URL_KEY) || 'null');
    sessionStorage.removeItem(RETURN_URL_KEY);
  } catch (e) {
    return false;
  }

  if (!saved || typeof saved.url !== 'string' || typeof saved.at !== 'number') return false;
  const age = Date.now() - saved.at;
  if (age < 0 || age > RETURN_URL_TTL_MS) return false;
  if (!saved.url.startsWith('/') || saved.url.startsWith('//')) return false;

  // Chỉ khôi phục khi vừa quay về từ trang đăng nhập (URL đúng là hash đã gửi cho máy chủ, không có query).
  if (window.location.search || window.location.hash !== saved.returnTo) return false;

  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === saved.url) return false;

  try {
    window.history.replaceState(window.history.state, '', saved.url);
    return true;
  } catch (e) {
    return false;
  }
}

export class AuthGate {
  /**
   * @param {Object} [options]
   * @param {Promise<{ok: boolean, status: number, data: any}>} [options.initialCheck]
   *   Kết quả /api/auth/me đã được gọi sớm trong <head> (chạy song song với việc tải JS).
   * @param {Function} [options.onLock] Gọi khi đang dùng mà bị khóa lại (đăng xuất / hết phiên).
   */
  constructor({ initialCheck = null, onLock = null } = {}) {
    this.root = document.getElementById('auth-gate') || this.createRoot();
    this.textEl = this.root.querySelector('[data-auth-gate-text]');
    this.errorEl = this.root.querySelector('[data-auth-gate-error]');

    this.user = null;
    this.state = 'checking'; // 'checking' | 'locked' | 'unlocked'
    this.hasUnlocked = false;
    this.onLock = onLock;
    this.authenticatedCallbacks = [];
    this.inertElements = new Set();
    this.bodyObserver = null;
    this.pendingCheck = null;
    this.lastCheckAt = 0;
    this.previousTitle = null;

    this.handleBlockedKey = this.handleBlockedKey.bind(this);

    this.lockDom();
    this.bindEvents();
    this.installFetchObserver();
    this.check(initialCheck);
  }

  /**
   * Dự phòng khi index.html chưa có sẵn markup cổng đăng nhập.
   */
  createRoot() {
    const root = document.createElement('div');
    root.id = 'auth-gate';
    root.className = 'auth-gate';
    root.dataset.state = 'checking';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'auth-gate-title');
    root.innerHTML = `
      <div class="auth-gate-card">
        <h1 id="auth-gate-title" class="auth-gate-title">Đăng nhập</h1>
        <p class="auth-gate-text" data-auth-gate-text></p>
        <div class="auth-gate-spinner" aria-hidden="true"></div>
        <button type="button" class="auth-gate-btn" data-auth-gate-action="login">Tiếp tục với Google</button>
        <button type="button" class="auth-gate-btn auth-gate-btn-secondary" data-auth-gate-action="retry">Thử lại</button>
        <p class="auth-gate-error" data-auth-gate-error role="alert"></p>
      </div>
    `;
    document.body.prepend(root);
    return root;
  }

  bindEvents() {
    this.root.addEventListener('click', event => {
      const button = event.target.closest('[data-auth-gate-action]');
      if (!button) return;
      event.preventDefault();
      if (button.dataset.authGateAction === 'login') this.login();
      if (button.dataset.authGateAction === 'retry') this.check(null, { showProgress: true });
    });

    // Đăng xuất (GoogleAccountController) hoặc Caro phát hiện mất phiên -> khóa lại ngay.
    window.addEventListener('google-account-changed', event => {
      const user = event.detail?.user || null;
      if (!user) {
        if (this.state === 'unlocked') this.lock();
      } else if (this.state !== 'unlocked') {
        this.check();
      }
    });

    // Đăng nhập ở tab khác rồi quay lại tab này -> tự kiểm tra lại.
    const recheckIfLocked = () => {
      if (this.state !== 'locked' || document.visibilityState === 'hidden') return;
      if (Date.now() - this.lastCheckAt < RECHECK_INTERVAL_MS) return;
      this.check();
    };
    window.addEventListener('focus', recheckIfLocked);
    document.addEventListener('visibilitychange', recheckIfLocked);
  }

  /**
   * Theo dõi (không thay đổi) phản hồi fetch: API của app trả 401 nghĩa là phiên đã hết hạn
   * hoặc đã đăng xuất ở tab khác -> kiểm tra lại và hiện cổng đăng nhập.
   */
  installFetchObserver() {
    if (typeof window.fetch !== 'function' || window.fetch.__authGateObserved) return;
    const nativeFetch = window.fetch;
    const gate = this;
    const observedFetch = function (...args) {
      return nativeFetch.apply(this, args).then(response => {
        try {
          if (response.status === 401 && gate.isProtectedApiUrl(response.url)) gate.handleUnauthorized();
        } catch (e) {}
        return response;
      });
    };
    observedFetch.__authGateObserved = true;
    window.fetch = observedFetch;
  }

  isProtectedApiUrl(url) {
    try {
      const parsed = new URL(url, window.location.href);
      return parsed.origin === window.location.origin
        && parsed.pathname.startsWith('/api/')
        && !parsed.pathname.startsWith('/api/auth/');
    } catch (e) {
      return false;
    }
  }

  handleUnauthorized() {
    if (this.state === 'unlocked') this.check();
  }

  async fetchMe() {
    const response = await fetch(ME_ENDPOINT, {
      cache: 'no-store',
      credentials: 'same-origin'
    });
    const data = await response.json();
    return { ok: response.ok, status: response.status, data };
  }

  /**
   * Kiểm tra phiên đăng nhập hiện tại với máy chủ.
   * @param {Promise|null} prefetched Kết quả đã gọi sẵn (chỉ dùng cho lần đầu).
   * @param {{ showProgress?: boolean }} [options] Hiện vòng xoay trong lúc kiểm tra (nút Thử lại).
   */
  check(prefetched = null, { showProgress = false } = {}) {
    if (this.pendingCheck) return this.pendingCheck;
    this.lastCheckAt = Date.now();
    if (showProgress && this.state !== 'unlocked') this.render('checking');

    this.pendingCheck = (async () => {
      try {
        const result = prefetched ? await prefetched : await this.fetchMe();
        const data = result?.data;
        if (!result?.ok || !data || typeof data !== 'object' || !Object.hasOwn(data, 'user')) {
          throw new Error(data?.error || `Không thể kiểm tra đăng nhập (${result?.status || 'lỗi mạng'}).`);
        }
        if (data.user) this.unlock(data.user);
        else this.lock();
      } catch (error) {
        console.error('[AuthGate] Không kiểm tra được phiên đăng nhập:', error);
        // Đang dùng mà lỗi mạng tạm thời thì không khóa; chưa đăng nhập thì báo lỗi kèm nút thử lại.
        if (this.state !== 'unlocked') {
          const message = error instanceof Error && error.message && !/JSON|Unexpected token|Failed to fetch|NetworkError|Load failed/i.test(error.message)
            ? error.message
            : 'Không kết nối được máy chủ đăng nhập.';
          this.lock(message);
        }
      } finally {
        this.pendingCheck = null;
      }
    })();
    return this.pendingCheck;
  }

  isUnlocked() {
    return this.state === 'unlocked';
  }

  /**
   * Chạy callback khi đã đăng nhập (ngay lập tức nếu đã mở khóa).
   * @param {Function} callback
   */
  whenAuthenticated(callback) {
    if (typeof callback !== 'function') return;
    if (this.state === 'unlocked') {
      callback(this.user);
      return;
    }
    this.authenticatedCallbacks.push(callback);
  }

  unlock(user) {
    this.user = user;
    if (this.state === 'unlocked') return;

    // Đã từng dùng, bị khóa (đăng xuất) rồi đăng nhập lại ở tab khác: tải lại trang để mọi app về trạng thái sạch.
    if (this.hasUnlocked) {
      this.state = 'unlocked';
      window.location.reload();
      return;
    }

    this.state = 'unlocked';
    this.hasUnlocked = true;
    this.unlockDom();
    if (this.previousTitle !== null && document.title === GATE_TITLE) document.title = this.previousTitle;
    this.previousTitle = null;

    const callbacks = this.authenticatedCallbacks.splice(0);
    for (const callback of callbacks) {
      try {
        callback(user);
      } catch (error) {
        console.error('[AuthGate] Lỗi khi khởi động app sau đăng nhập:', error);
      }
    }
  }

  lock(errorMessage = null) {
    const wasUnlocked = this.state === 'unlocked';
    this.user = null;
    this.state = 'locked';
    this.lockDom();
    this.render(errorMessage ? 'error' : 'signin', errorMessage);
    if (this.previousTitle === null) this.previousTitle = document.title;
    document.title = GATE_TITLE;

    if (wasUnlocked && typeof this.onLock === 'function') {
      try {
        this.onLock();
      } catch (error) {
        console.warn('[AuthGate] Lỗi khi dừng app lúc khóa:', error);
      }
    }
  }

  login() {
    const returnTo = safeReturnHash(window.location.hash);
    try {
      sessionStorage.setItem(RETURN_URL_KEY, JSON.stringify({
        url: window.location.pathname + window.location.search + window.location.hash,
        returnTo,
        at: Date.now()
      }));
    } catch (e) {}
    window.location.assign(`${LOGIN_ENDPOINT}?returnTo=${encodeURIComponent(returnTo)}`);
  }

  render(state, errorMessage = null) {
    this.root.dataset.state = state;
    if (this.textEl) {
      this.textEl.textContent = state === 'checking'
        ? 'Đang kiểm tra phiên đăng nhập...'
        : 'Bạn cần đăng nhập Google để sử dụng các ứng dụng trong Workspace.';
    }
    if (this.errorEl) this.errorEl.textContent = state === 'error' ? (errorMessage || '') : '';
    if (state === 'signin' || state === 'error') {
      const target = this.root.querySelector('[data-auth-gate-action="login"]');
      if (target && !this.root.contains(document.activeElement)) {
        try { target.focus({ preventScroll: true }); } catch (e) {}
      }
    }
  }

  // ---------------------------------------------------------------------------
  // KHÓA / MỞ GIAO DIỆN PHÍA SAU CỔNG ĐĂNG NHẬP
  // ---------------------------------------------------------------------------

  lockDom() {
    this.root.hidden = false;
    document.documentElement.classList.add('auth-locked');
    for (const element of document.body.children) this.makeInert(element);

    if (!this.bodyObserver && typeof MutationObserver === 'function') {
      this.bodyObserver = new MutationObserver(records => {
        for (const record of records) {
          for (const node of record.addedNodes) this.makeInert(node);
        }
      });
      this.bodyObserver.observe(document.body, { childList: true });
    }

    // Pha capture của document: chạy sau SecurityGuard (capture của window), trước mọi phím tắt của app.
    document.removeEventListener('keydown', this.handleBlockedKey, true);
    document.removeEventListener('keyup', this.handleBlockedKey, true);
    document.removeEventListener('keypress', this.handleBlockedKey, true);
    document.addEventListener('keydown', this.handleBlockedKey, true);
    document.addEventListener('keyup', this.handleBlockedKey, true);
    document.addEventListener('keypress', this.handleBlockedKey, true);
  }

  unlockDom() {
    this.bodyObserver?.disconnect();
    this.bodyObserver = null;
    for (const element of this.inertElements) element.removeAttribute('inert');
    this.inertElements.clear();
    document.removeEventListener('keydown', this.handleBlockedKey, true);
    document.removeEventListener('keyup', this.handleBlockedKey, true);
    document.removeEventListener('keypress', this.handleBlockedKey, true);
    document.documentElement.classList.remove('auth-locked');
    this.root.hidden = true;
  }

  makeInert(node) {
    if (!(node instanceof HTMLElement) || node === this.root) return;
    if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE' || node.tagName === 'LINK') return;
    if (node.hasAttribute('inert')) return;
    node.setAttribute('inert', '');
    this.inertElements.add(node);
  }

  handleBlockedKey(event) {
    if (this.state === 'unlocked') return;
    // Không chặn hành vi mặc định (Tab / Enter trên nút đăng nhập vẫn chạy), chỉ chặn phím tắt của app.
    event.stopImmediatePropagation();
  }
}
